export interface WorldLocation {
  id: string;
  name: string;
  x: number;
  y: number;
  type: 'city' | 'dungeon' | 'wilderness' | 'camp' | 'landmark';
  levelRange: [number, number];
  enemies: string[];
  loot: string[];
  weather: 'clear' | 'stormy' | 'foggy' | 'dark';
}

export interface EncounterSpawner {
  id: string;
  locationId: string;
  enemyId: string;
  spawnRate: number; // 0-1 chance per visit
  level: number;
}

export interface WorldEvent {
  id: string;
  name: string;
  description: string;
  type: 'combat' | 'treasure' | 'npc' | 'environmental';
  probability: number;
  reward?: { gold: number; experience: number };
}

export const WORLD_LOCATIONS: WorldLocation[] = [
  {
    id: 'red-market',
    name: 'The Red Market',
    x: 40,
    y: 30,
    type: 'city',
    levelRange: [1, 5],
    enemies: ['shadow-beast'],
    loot: ['Health Potion', 'Antidote'],
    weather: 'clear',
  },
  {
    id: 'lazerate',
    name: 'The Lazerate',
    x: 35,
    y: 50,
    type: 'dungeon',
    levelRange: [3, 8],
    enemies: ['corrupted-mage', 'shadow-beast'],
    loot: ['Mana Crystal', 'Ancient Coin'],
    weather: 'dark',
  },
  {
    id: 'vestuvia',
    name: 'Vestuvia',
    x: 60,
    y: 45,
    type: 'city',
    levelRange: [2, 6],
    enemies: ['shadow-beast'],
    loot: ['Gold Coin', 'Silk'],
    weather: 'clear',
  },
  {
    id: 'oasis',
    name: 'The Oasis',
    x: 20,
    y: 70,
    type: 'wilderness',
    levelRange: [4, 10],
    enemies: ['void-knight', 'corrupted-mage'],
    loot: ['Water Flask', 'Desert Rose'],
    weather: 'clear',
  },
  {
    id: 'single-cell-biom',
    name: 'The Single Cell Biom',
    x: 70,
    y: 25,
    type: 'wilderness',
    levelRange: [5, 12],
    enemies: ['void-knight'],
    loot: ['Biomatter', 'Cell Sample'],
    weather: 'foggy',
  },
  {
    id: 'badlands',
    name: 'The Badlands',
    x: 50,
    y: 75,
    type: 'wilderness',
    levelRange: [6, 15],
    enemies: ['void-knight'],
    loot: ['Obsidian', 'Dark Crystal'],
    weather: 'stormy',
  },
  {
    id: 'mushroom-caves',
    name: 'The Mushroom Caves',
    x: 15,
    y: 40,
    type: 'dungeon',
    levelRange: [3, 9],
    enemies: ['corrupted-mage', 'shadow-beast'],
    loot: ['Mushroom', 'Spore Powder'],
    weather: 'dark',
  },
  {
    id: 'bioluminescent-caves',
    name: 'The Bioluminescent Caves',
    x: 25,
    y: 60,
    type: 'dungeon',
    levelRange: [4, 11],
    enemies: ['void-knight', 'corrupted-mage'],
    loot: ['Luminous Orb', 'Glowing Crystal'],
    weather: 'dark',
  },
  {
    id: 'split-mountains',
    name: 'The Split Mountains',
    x: 75,
    y: 60,
    type: 'landmark',
    levelRange: [7, 14],
    enemies: ['void-knight'],
    loot: ['Mountain Stone', 'Eagle Feather'],
    weather: 'stormy',
  },
  {
    id: 'ecological-dead-zone',
    name: 'The Ecological Dead Zone',
    x: 80,
    y: 80,
    type: 'wilderness',
    levelRange: [10, 20],
    enemies: ['void-knight'],
    loot: ['Void Essence', 'Dead Material'],
    weather: 'dark',
  },
];

export const WORLD_EVENTS: WorldEvent[] = [
  {
    id: 'treasure-chest',
    name: 'Hidden Treasure Chest',
    description: 'You discover a mysterious chest glowing with arcane energy!',
    type: 'treasure',
    probability: 0.15,
    reward: { gold: 100, experience: 50 },
  },
  {
    id: 'lone-warrior',
    name: 'Lone Warrior',
    description: 'A weary warrior approaches you seeking aid.',
    type: 'npc',
    probability: 0.1,
  },
  {
    id: 'ancient-ruins',
    name: 'Ancient Ruins',
    description: 'You stumble upon crumbling structures from a forgotten age.',
    type: 'environmental',
    probability: 0.12,
  },
  {
    id: 'monster-den',
    name: 'Monster Den',
    description: 'A horde of creatures emerges from the shadows!',
    type: 'combat',
    probability: 0.2,
    reward: { gold: 150, experience: 75 },
  },
  {
    id: 'magical-anomaly',
    name: 'Magical Anomaly',
    description: 'Reality itself seems to tear apart here.',
    type: 'environmental',
    probability: 0.08,
  },
];

export const LOOT_TABLE = {
  'Health Potion': { rarity: 'common', value: 10 },
  'Mana Potion': { rarity: 'common', value: 15 },
  'Antidote': { rarity: 'uncommon', value: 25 },
  'Mana Crystal': { rarity: 'uncommon', value: 50 },
  'Ancient Coin': { rarity: 'rare', value: 100 },
  'Gold Coin': { rarity: 'common', value: 20 },
  'Silk': { rarity: 'uncommon', value: 35 },
  'Water Flask': { rarity: 'common', value: 5 },
  'Desert Rose': { rarity: 'uncommon', value: 40 },
  'Biomatter': { rarity: 'uncommon', value: 45 },
  'Cell Sample': { rarity: 'rare', value: 80 },
  'Obsidian': { rarity: 'uncommon', value: 55 },
  'Dark Crystal': { rarity: 'rare', value: 120 },
  'Mushroom': { rarity: 'common', value: 8 },
  'Spore Powder': { rarity: 'uncommon', value: 30 },
  'Luminous Orb': { rarity: 'rare', value: 110 },
  'Glowing Crystal': { rarity: 'rare', value: 115 },
  'Mountain Stone': { rarity: 'uncommon', value: 60 },
  'Eagle Feather': { rarity: 'uncommon', value: 50 },
  'Void Essence': { rarity: 'legendary', value: 500 },
  'Dead Material': { rarity: 'rare', value: 150 },
};
